import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Link,
  useSearchParams,
} from 'react-router-dom';

import serviciosData from '../../data/Serviciosdata';
import { useIdioma } from '../../context/IdiomaContext';

import './Contacto.css';

function Contacto() {
  const { idioma, t } = useIdioma();

  const [searchParams] = useSearchParams();

  const servicioUrl =
    searchParams.get('servicio');

  const opcionUrl =
    searchParams.get('opcion');

  const obtenerTextoTraducido = useCallback(
    (contenido) => {
      if (
        typeof contenido === 'object' &&
        contenido !== null
      ) {
        return (
          contenido[idioma] ||
          contenido.es ||
          ''
        );
      }

      return contenido;
    },
    [idioma]
  );

  const asuntos = useMemo(() => {
    const listaAsuntos = [
      {
        value: 'consulta-general',

        label: t(
          'contacto.formulario.consultaGeneral'
        ),

        servicioId: null,
        opcionId: null,
      },
    ];

    serviciosData.forEach((servicio) => {
      servicio.opciones?.forEach(
        (opcion) => {
          listaAsuntos.push({
            value: `${servicio.id}:${opcion.id}`,

            label: `${obtenerTextoTraducido(
              servicio.titulo
            )} — ${obtenerTextoTraducido(
              opcion.titulo
            )}`,

            servicioId: servicio.id,
            opcionId: opcion.id,
          });
        }
      );
    });

    listaAsuntos.push({
      value: 'otro',

      label: t(
        'contacto.formulario.otroAsunto'
      ),

      servicioId: null,
      opcionId: null,
    });

    return listaAsuntos;
  }, [obtenerTextoTraducido, t]);

  const asuntoInicial = useMemo(() => {
    if (servicioUrl && opcionUrl) {
      const valorOpcion =
        `${servicioUrl}:${opcionUrl}`;

      const existeOpcion = asuntos.some(
        (asunto) =>
          asunto.value === valorOpcion
      );

      if (existeOpcion) {
        return valorOpcion;
      }
    }

    if (servicioUrl) {
      const primeraOpcionDelServicio =
        asuntos.find(
          (asunto) =>
            asunto.servicioId === servicioUrl
        );

      if (primeraOpcionDelServicio) {
        return primeraOpcionDelServicio.value;
      }
    }

    return 'consulta-general';
  }, [asuntos, servicioUrl, opcionUrl]);

  const [formulario, setFormulario] =
    useState({
      nombre: '',
      email: '',
      asunto: asuntoInicial,
      asuntoPersonalizado: '',
      fecha: '',
      hora: '',
      mensaje: '',
      privacidad: false,
    });

  const [estadoEnvio, setEstadoEnvio] =
    useState('idle');

  const [mensajeEstado, setMensajeEstado] =
    useState('');

  useEffect(() => {
    setFormulario((formularioActual) => ({
      ...formularioActual,
      asunto: asuntoInicial,
    }));
  }, [asuntoInicial]);

  const fechaMinima = useMemo(() => {
    const hoy = new Date();

    hoy.setMinutes(
      hoy.getMinutes() -
        hoy.getTimezoneOffset()
    );

    return hoy.toISOString().split('T')[0];
  }, []);

  const controlarCambio = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setFormulario((formularioActual) => ({
      ...formularioActual,

      [name]:
        type === 'checkbox'
          ? checked
          : value,
    }));

    if (estadoEnvio !== 'idle') {
      setEstadoEnvio('idle');
      setMensajeEstado('');
    }
  };

  const enviarFormulario = async (event) => {
    event.preventDefault();

    setEstadoEnvio('sending');
    setMensajeEstado('');

    const asuntoSeleccionado =
      asuntos.find(
        (asunto) =>
          asunto.value === formulario.asunto
      );

    const asuntoFinal =
      formulario.asunto === 'otro'
        ? formulario.asuntoPersonalizado.trim()
        : asuntoSeleccionado?.label;

    const [servicioId, opcionId] =
      formulario.asunto.includes(':')
        ? formulario.asunto.split(':')
        : [formulario.asunto, null];

    const datosSolicitud = {
      nombre: formulario.nombre.trim(),
      email: formulario.email.trim(),
      asunto: asuntoFinal,

      servicioId:
        servicioId === 'consulta-general' ||
        servicioId === 'otro'
          ? null
          : servicioId,

      opcionId,
      fecha: formulario.fecha,
      hora: formulario.hora,
      zonaHoraria: 'Europe/Madrid',
      mensaje: formulario.mensaje.trim(),
      privacidad: formulario.privacidad,
      idioma,
    };

    try {
      const respuesta = await fetch(
        '/api/contacto',
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',
          },

          body: JSON.stringify(
            datosSolicitud
          ),
        }
      );

      const resultado =
        await respuesta.json();

      if (!respuesta.ok) {
        console.error(
          'Error al enviar el formulario:',
          resultado
        );

        throw new Error(
          t('contacto.estado.error')
        );
      }

      setEstadoEnvio('success');

      setMensajeEstado(
        t('contacto.estado.exito')
      );

      setFormulario({
        nombre: '',
        email: '',
        asunto: 'consulta-general',
        asuntoPersonalizado: '',
        fecha: '',
        hora: '',
        mensaje: '',
        privacidad: false,
      });
    } catch (error) {
      console.error(
        'Error del formulario:',
        error
      );

      setEstadoEnvio('error');

      setMensajeEstado(
        error.message ||
          t('contacto.estado.error')
      );
    }
  };

  const enviando =
    estadoEnvio === 'sending';

  return (
    <section className="pagina-contacto">
      <div className="contacto-presentacion">
        <p className="contacto-etiqueta">
          {t('contacto.etiqueta')}
        </p>

        <h1>
          {t('contacto.titulo')}
        </h1>

        <span>
          {t('contacto.descripcion')}
        </span>

        <p className="contacto-aclaracion">
          {t('contacto.aclaracion')}
        </p>
      </div>

      <form
        className="contacto-formulario"
        onSubmit={enviarFormulario}
      >
        <div className="contacto-campo">
          <label htmlFor="nombre">
            {t(
              'contacto.formulario.nombre'
            )}
          </label>

          <input
            id="nombre"
            name="nombre"
            type="text"
            value={formulario.nombre}
            onChange={controlarCambio}
            autoComplete="name"
            maxLength="100"
            required
          />
        </div>

        <div className="contacto-campo">
          <label htmlFor="email">
            {t(
              'contacto.formulario.email'
            )}
          </label>

          <input
            id="email"
            name="email"
            type="email"
            value={formulario.email}
            onChange={controlarCambio}
            autoComplete="email"
            maxLength="150"
            required
          />
        </div>

        <div className="contacto-campo">
          <label htmlFor="asunto">
            {t(
              'contacto.formulario.asunto'
            )}
          </label>

          <select
            id="asunto"
            name="asunto"
            value={formulario.asunto}
            onChange={controlarCambio}
            required
          >
            <option value="consulta-general">
              {t(
                'contacto.formulario.consultaGeneral'
              )}
            </option>

            {serviciosData.map(
              (servicio) => (
                <optgroup
                  label={obtenerTextoTraducido(
                    servicio.titulo
                  )}
                  key={servicio.id}
                >
                  {servicio.opciones?.map(
                    (opcion) => (
                      <option
                        value={`${servicio.id}:${opcion.id}`}
                        key={opcion.id}
                      >
                        {obtenerTextoTraducido(
                          opcion.titulo
                        )}
                      </option>
                    )
                  )}
                </optgroup>
              )
            )}

            <option value="otro">
              {t(
                'contacto.formulario.otroAsunto'
              )}
            </option>
          </select>
        </div>

        {formulario.asunto === 'otro' && (
          <div className="contacto-campo">
            <label htmlFor="asuntoPersonalizado">
              {t(
                'contacto.formulario.escribirAsunto'
              )}
            </label>

            <input
              id="asuntoPersonalizado"
              name="asuntoPersonalizado"
              type="text"
              value={
                formulario.asuntoPersonalizado
              }
              onChange={controlarCambio}
              maxLength="150"
              required
            />
          </div>
        )}

        <div className="contacto-fecha-hora">
          <div className="contacto-campo">
            <label htmlFor="fecha">
              {t(
                'contacto.formulario.fecha'
              )}
            </label>

            <input
              id="fecha"
              name="fecha"
              type="date"
              min={fechaMinima}
              value={formulario.fecha}
              onChange={controlarCambio}
              required
            />
          </div>

          <div className="contacto-campo">
            <label htmlFor="hora">
              {t(
                'contacto.formulario.hora'
              )}
            </label>

            <input
              id="hora"
              name="hora"
              type="time"
              value={formulario.hora}
              onChange={controlarCambio}
              required
            />
          </div>
        </div>

        <div className="contacto-campo">
          <label htmlFor="mensaje">
            {t(
              'contacto.formulario.mensaje'
            )}
          </label>

          <textarea
            id="mensaje"
            name="mensaje"
            rows="7"
            value={formulario.mensaje}
            onChange={controlarCambio}
            maxLength="2000"
            required
          />
        </div>

        <label className="contacto-privacidad">
          <input
            name="privacidad"
            type="checkbox"
            checked={formulario.privacidad}
            onChange={controlarCambio}
            required
          />

          <span>
            {t(
              'contacto.formulario.privacidadInicio'
            )}{' '}

            <Link to="/privacidad">
              {t(
                'contacto.formulario.privacidadEnlace'
              )}
            </Link>
            .
          </span>
        </label>

        <button
          type="submit"
          disabled={enviando}
        >
          {enviando
            ? t(
                'contacto.formulario.enviando'
              )
            : t(
                'contacto.formulario.enviar'
              )}
        </button>

        {mensajeEstado && (
          <p
            className={`contacto-estado contacto-estado-${estadoEnvio}`}
            role="status"
            aria-live="polite"
          >
            {mensajeEstado}
          </p>
        )}
      </form>
    </section>
  );
}

export default Contacto;